/**
 * Decoder for the AWS `vnd.amazon.eventstream` binary framing that
 * `InvokeModelWithResponseStream` replies in.
 *
 * There is no streaming decoder in aws4fetch and pulling @aws-sdk/client-bedrock-runtime
 * into a Worker costs hundreds of kilobytes for one wire format, so it lives here.
 *
 * FRAME LAYOUT
 *
 *   0                   4                   8                  12
 *   +-------------------+-------------------+-------------------+
 *   | total length (BE) | headers length    | prelude CRC32     |
 *   +-------------------+-------------------+-------------------+
 *   | headers (headers length bytes)                            |
 *   | payload (total - headers length - 16 bytes)               |
 *   | message CRC32 (4 bytes)                                   |
 *   +-----------------------------------------------------------+
 *
 * Headers are skipped rather than parsed: the only one that matters
 * (`:event-type`) is redundant, because the decoded payload carries its own
 * `type` field. CRCs are not verified — the transport is TLS over HTTP/2, which
 * already guarantees integrity, and a mismatch here would be unrecoverable
 * anyway.
 *
 * The payload is JSON `{"bytes": "<base64>"}` wrapping the actual Anthropic
 * streaming event.
 */

const PRELUDE_BYTES = 12;
const MESSAGE_CRC_BYTES = 4;
const MIN_FRAME_BYTES = PRELUDE_BYTES + MESSAGE_CRC_BYTES;

/**
 * Hard ceiling on a single frame, as a guard against a corrupt length prefix
 * making us buffer unboundedly. Bedrock frames are a few KB; 16 MB is far
 * beyond any real one and far below anything that would hurt a Worker.
 */
const MAX_FRAME_BYTES = 16 * 1024 * 1024;

/** One decoded Bedrock event: the inner Anthropic streaming event object. */
export type BedrockEvent = Record<string, unknown>;

function decodeBase64(b64: string): string {
  // atob is present in Workers and in Node 18+. TextDecoder handles the
  // multi-byte characters atob's latin1 output would otherwise mangle — an
  // emoji or a curly quote in a model's answer must not corrupt the stream.
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

/**
 * Turn a raw byte stream into decoded Bedrock events.
 *
 * A chunk boundary can fall anywhere, including inside the 12-byte prelude, so
 * bytes are buffered until a whole frame is present. Getting this wrong
 * produces a stream that works in testing and truncates under load.
 */
export async function* decodeEventStream(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<BedrockEvent> {
  const reader = body.getReader();
  let buf = new Uint8Array(0);

  const append = (chunk: Uint8Array) => {
    const next = new Uint8Array(buf.length + chunk.length);
    next.set(buf, 0);
    next.set(chunk, buf.length);
    buf = next;
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (value) append(value);

      for (;;) {
        if (buf.length < PRELUDE_BYTES) break;

        const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
        const total = view.getUint32(0, false);
        const headersLen = view.getUint32(4, false);

        if (total < MIN_FRAME_BYTES || total > MAX_FRAME_BYTES) {
          throw new Error(`Malformed event stream frame: length ${total}`);
        }
        if (buf.length < total) break;

        const start = PRELUDE_BYTES + headersLen;
        const end = total - MESSAGE_CRC_BYTES;
        if (start <= end) {
          const payload = new TextDecoder().decode(buf.subarray(start, end));
          const event = parseFramePayload(payload);
          if (event) yield event;
        }

        buf = buf.slice(total);
      }

      if (done) break;
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Unwrap one frame payload.
 *
 * Returns null rather than throwing on junk: a single unreadable frame in a
 * long answer should drop that frame, not abort a response the user is already
 * reading.
 */
export function parseFramePayload(payload: string): BedrockEvent | null {
  let outer: { bytes?: unknown; message?: unknown };
  try {
    outer = JSON.parse(payload) as typeof outer;
  } catch {
    return null;
  }

  // Exception frames (throttling, validation) carry `message` and no `bytes`.
  if (typeof outer.bytes !== "string") {
    return typeof outer.message === "string" ? { type: "error", message: outer.message } : null;
  }

  try {
    return JSON.parse(decodeBase64(outer.bytes)) as BedrockEvent;
  } catch {
    return null;
  }
}

/**
 * Pull the text out of an Anthropic streaming event, or null when the event
 * carries none (message_start, ping, content_block_stop, …).
 */
export function textDelta(event: BedrockEvent): string | null {
  if (event.type !== "content_block_delta") return null;
  const delta = event.delta as { type?: string; text?: string } | undefined;
  if (delta?.type !== "text_delta" || typeof delta.text !== "string") return null;
  return delta.text;
}
